import React, { useState } from 'react'
import { Box, IconButton, Tooltip, Divider, Typography, useTheme } from '@mui/material'
import { motion, AnimatePresence } from 'framer-motion'
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong'
import MyLocationIcon from '@mui/icons-material/MyLocation'
import TimelineIcon from '@mui/icons-material/Timeline'
import TerrainIcon from '@mui/icons-material/Terrain'
import MapIcon from '@mui/icons-material/Map'
import LayersIcon from '@mui/icons-material/Layers'
import SatelliteAltIcon from '@mui/icons-material/SatelliteAlt'
import ExploreIcon from '@mui/icons-material/Explore'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import PublicIcon from '@mui/icons-material/Public'

// Arc fans upward-right from the Layers button, radius = 130px.
// Angles: 2°, 30°, 58°, 86° — all x > 0, center-to-center ~63px (> 44px button width, no overlap).
// x = 130·cos(θ),  y = −130·sin(θ)
const MAP_STYLES = [
  {
    type: 'standard',
    label: 'Default',
    icon: PublicIcon,
    url: null,
    arc: { x: 130, y: -5 },    // 2°  — nearly straight right
  },
  {
    type: 'satellite',
    label: 'Satellite',
    icon: SatelliteAltIcon,
    url: 'mapbox://styles/mapbox/satellite-streets-v12',
    arc: { x: 113, y: -65 },   // 30°
  },
  {
    type: 'navigation',
    label: 'Navigate',
    icon: ExploreIcon,
    url: 'mapbox://styles/mapbox/navigation-day-v1',
    arc: { x: 69, y: -110 },   // 58°
  },
  {
    type: 'dark',
    label: 'Dark',
    icon: DarkModeIcon,
    url: 'mapbox://styles/mapbox/dark-v11',
    arc: { x: 9, y: -130 },    // 86°  — nearly straight up
  },
]

function MapControls({
  nodes,
  mapRef,
  connectMode,
  onToggleConnectMode,
  showTerrain,
  onToggleTerrain,
  showMinimap,
  onToggleMinimap,
  mapStyleType,
  onChangeMapStyle,
}) {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const [stylePickerOpen, setStylePickerOpen] = useState(false)

  const handleFitToNetwork = () => {
    const mapNodes = nodes.filter(n => n.data.lat != null && n.data.lng != null)
    if (mapNodes.length === 0) return
    if (mapNodes.length === 1) {
      mapRef.current?.flyTo({ center: [mapNodes[0].data.lng, mapNodes[0].data.lat], zoom: 8, duration: 1000 })
      return
    }
    const bounds = mapNodes.reduce(
      (acc, node) => ({
        minLat: Math.min(acc.minLat, node.data.lat), maxLat: Math.max(acc.maxLat, node.data.lat),
        minLng: Math.min(acc.minLng, node.data.lng), maxLng: Math.max(acc.maxLng, node.data.lng),
      }),
      { minLat: 90, maxLat: -90, minLng: 180, maxLng: -180 }
    )
    mapRef.current?.fitBounds(
      [[bounds.minLng, bounds.minLat], [bounds.maxLng, bounds.maxLat]],
      { padding: 80, duration: 1000 }
    )
  }

  const handleCenterIndia = () => {
    mapRef.current?.flyTo({ center: [78.9629, 20.5937], zoom: 4.5, duration: 1200 })
  }

  const cardBg = isDark ? 'rgba(15, 23, 42, 0.92)' : 'rgba(255, 255, 255, 0.95)'
  const cardBorder = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'
  const cardShadow = isDark ? '0 4px 16px rgba(0,0,0,0.4)' : '0 4px 16px rgba(0,0,0,0.1)'

  const btnSx = (active) => ({
    borderRadius: 0,
    width: 36,
    height: 36,
    color: active ? (isDark ? '#a5b4fc' : '#4338ca') : (isDark ? '#e2e8f0' : '#374151'),
    bgcolor: active ? (isDark ? 'rgba(129,140,248,0.15)' : 'rgba(67,56,202,0.08)') : 'transparent',
    '&:hover': {
      bgcolor: active
        ? (isDark ? 'rgba(129,140,248,0.25)' : 'rgba(67,56,202,0.12)')
        : (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'),
    },
  })

  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: 32,
        left: 16,
        zIndex: 5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        alignItems: 'center',
      }}
    >
      {/* ── Arc map style picker ── */}
      {/* Wrapper is 36×36; arc buttons are absolutely positioned and animated outward */}
      <Box sx={{ position: 'relative', width: 36, height: 36 }}>
        <AnimatePresence>
          {stylePickerOpen && MAP_STYLES.map(({ type, label, icon: Icon, arc }, i) => {
            const isActive = mapStyleType === type || (!mapStyleType && type === 'standard')
            return (
              <motion.div
                key={type}
                initial={{ opacity: 0, x: 0, y: 0, scale: 0.35 }}
                animate={{ opacity: 1, x: arc.x, y: arc.y, scale: 1 }}
                exit={{ opacity: 0, x: 0, y: 0, scale: 0.35 }}
                transition={{ type: 'spring', stiffness: 400, damping: 28, delay: i * 0.045 }}
                style={{ position: 'absolute', top: 0, left: 0, zIndex: 20 }}
              >
                <Tooltip title={label} placement="top" arrow>
                  <Box
                    onClick={() => { onChangeMapStyle(type === 'standard' ? null : type); setStylePickerOpen(false) }}
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: '12px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.3,
                      cursor: 'pointer',
                      bgcolor: isActive ? '#4f46e5' : cardBg,
                      border: isActive
                        ? '2px solid #a5b4fc'
                        : `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)'}`,
                      boxShadow: isActive
                        ? '0 4px 16px rgba(79,70,229,0.45)'
                        : cardShadow,
                      backdropFilter: 'blur(12px)',
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        bgcolor: isActive ? '#4338ca' : (isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'),
                        transform: 'scale(1.08)',
                      },
                    }}
                  >
                    <Icon sx={{ fontSize: 16, color: isActive ? '#fff' : (isDark ? '#e2e8f0' : '#374151') }} />
                    <Typography sx={{
                      fontSize: '8px', fontWeight: 700, lineHeight: 1,
                      color: isActive ? '#fff' : (isDark ? '#94a3b8' : '#6b7280'),
                    }}>
                      {label}
                    </Typography>
                  </Box>
                </Tooltip>
              </motion.div>
            )
          })}
        </AnimatePresence>

        {/* Layers toggle button */}
        <Tooltip title="Switch map style" placement="right" arrow>
          <Box
            onClick={() => setStylePickerOpen(o => !o)}
            sx={{
              position: 'absolute', top: 0, left: 0,
              width: 36, height: 36,
              borderRadius: '10px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer',
              bgcolor: stylePickerOpen ? '#4f46e5' : cardBg,
              border: stylePickerOpen
                ? '1px solid #a5b4fc'
                : `1px solid ${cardBorder}`,
              boxShadow: cardShadow,
              backdropFilter: 'blur(12px)',
              transition: 'all 0.2s ease',
              zIndex: 21,
              '&:hover': {
                bgcolor: stylePickerOpen ? '#4338ca' : (isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.06)'),
              },
            }}
          >
            <motion.div
              animate={{ rotate: stylePickerOpen ? 45 : 0 }}
              transition={{ duration: 0.22 }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <LayersIcon sx={{ fontSize: 18, color: stylePickerOpen ? '#fff' : (isDark ? '#e2e8f0' : '#374151') }} />
            </motion.div>
          </Box>
        </Tooltip>
      </Box>

      {/* ── Main control button panel ── */}
      <Box
        sx={{
          bgcolor: cardBg,
          backdropFilter: 'blur(12px)',
          borderRadius: '10px',
          border: `1px solid ${cardBorder}`,
          overflow: 'hidden',
          boxShadow: cardShadow,
        }}
      >
        <Tooltip title="Fit to network" placement="right" arrow>
          <IconButton onClick={handleFitToNetwork} sx={btnSx(false)}>
            <CenterFocusStrongIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>

        <Divider sx={{ mx: 0.5 }} />

        <Tooltip title="Center on India" placement="right" arrow>
          <IconButton onClick={handleCenterIndia} sx={btnSx(false)}>
            <MyLocationIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>

        <Divider sx={{ mx: 0.5 }} />

        <Tooltip title={connectMode.active ? 'Cancel connect' : 'Connect nodes'} placement="right" arrow>
          <IconButton onClick={onToggleConnectMode} sx={btnSx(connectMode.active)}>
            <TimelineIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>

        <Divider sx={{ mx: 0.5 }} />

        <Tooltip title={showTerrain ? 'Disable 3D terrain' : 'Enable 3D terrain'} placement="right" arrow>
          <IconButton onClick={onToggleTerrain} sx={btnSx(showTerrain)}>
            <TerrainIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>

        <Divider sx={{ mx: 0.5 }} />

        <Tooltip title={showMinimap ? 'Hide minimap' : 'Show minimap'} placement="right" arrow>
          <IconButton onClick={onToggleMinimap} sx={btnSx(showMinimap)}>
            <MapIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  )
}

export default React.memo(MapControls)
